import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, ShieldCheck, ExternalLink } from "lucide-react";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: "Forside", path: "/" },
    { name: "Menukort", path: "/menukort" },
    { name: "Bestilling", path: "/bestilling" },
    { name: "Firmaordning", path: "/firmaordning" },
    { name: "Om os", path: "/om-os" },
    { name: "Galleri", path: "/galleri" },
    { name: "Kontakt", path: "/kontakt" },
  ];

  const isActive = (path) => location.pathname === path;

  // Lock body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <header className={`w-full bg-neutral-900 text-white sticky top-0 border-b border-neutral-800 shadow-md transition-all duration-300 ${isOpen ? "z-50" : "z-40"}`}>
      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col items-center gap-4">
        {/* Logo & Mobile layout */}
        <div className="w-full flex justify-between items-center lg:justify-center relative">
          {/* Centered Logo on Desktop, Left-aligned on Mobile */}
          <Link to="/" className="flex items-center justify-start lg:justify-center">
            <img
              src="https://assets.auxodev.dk/assets/0fa1183b-3f51-46de-aff6-df593e862933/90f5eb87-5ef2-4351-8a0f-2734b57377f4.png"
              alt="Hillerød Smørrebrød Logo"
              className="h-10 md:h-14 w-auto object-contain transition-all duration-300 brightness-0 invert"
            />
          </Link>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsOpen(true)}
            className="lg:hidden p-2 text-neutral-400 hover:text-white focus:outline-none transition-colors"
            aria-label="Åbn menu"
          >
            <Menu size={24} />
          </button>
        </div>

        {/* Desktop Nav - Centered below logo */}
        <nav className="hidden lg:flex items-center justify-center gap-10 border-t border-neutral-800 pt-4 w-full max-w-3xl">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`text-xs tracking-widest uppercase transition-all duration-200 pb-1 font-bold text-white border-b-2 ${
                isActive(item.path) ? "border-white" : "border-transparent hover:border-white/40"
              }`}
            >
              {item.name}
            </Link>
          ))}
        </nav>
      </div>

      {/* Mobile Sidebar Menu (Slide-out Drawer) */}
      <div
        className={`fixed inset-0 z-50 lg:hidden transition-all duration-300 ${
          isOpen ? "visible pointer-events-auto" : "invisible pointer-events-none"
        }`}
      >
        {/* Backdrop Overlay */}
        <div
          className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
            isOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setIsOpen(false)}
        />

        {/* Drawer Container */}
        <div
          className={`absolute top-0 right-0 h-full w-80 max-w-[85vw] bg-neutral-950 border-l border-neutral-800 shadow-2xl flex flex-col justify-between p-6 transition-transform duration-300 ease-in-out ${
            isOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Drawer Header */}
          <div className="flex items-center justify-between pb-6 border-b border-neutral-800">
            <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center">
              <img
                src="https://assets.auxodev.dk/assets/0fa1183b-3f51-46de-aff6-df593e862933/90f5eb87-5ef2-4351-8a0f-2734b57377f4.png"
                alt="Hillerød Smørrebrød Logo"
                className="h-8 w-auto object-contain brightness-0 invert"
              />
            </Link>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 text-neutral-400 hover:text-white hover:bg-neutral-900 rounded-full transition-all duration-200"
              aria-label="Luk menu"
            >
              <X size={22} />
            </button>
          </div>

          {/* Drawer Navigation Links */}
          <nav className="flex flex-col gap-1 py-6 overflow-y-auto flex-grow">
            {navItems.map((item) => {
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`text-sm tracking-wider uppercase py-3 px-4 transition-all duration-200 flex items-center justify-between text-white font-bold border-b-2 ${
                    isActive(item.path) ? "border-white" : "border-transparent"
                  }`}
                >
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Drawer Footer / Contact Info */}
          <div className="pt-6 border-t border-neutral-800 flex flex-col gap-5">
            <a
              href="tel:48266613"
              className="w-full text-center py-3.5 text-xs font-bold uppercase tracking-widest bg-[#2d5a27] hover:bg-[#20401c] text-white transition-all duration-300 rounded-lg shadow-lg shadow-emerald-950/20 flex items-center justify-center gap-2"
            >
              <Phone size={14} />
              <span>Ring & Bestil: 48 26 66 13</span>
            </a>

            <div className="flex flex-col gap-3 text-xs text-neutral-400">
              <div className="flex items-start gap-2.5">
                <MapPin size={14} className="text-neutral-500 shrink-0 mt-0.5" />
                <span>Frederiksværksgade 63, 3400 Hillerød</span>
              </div>
              <div className="flex items-start gap-2.5">
                <Mail size={14} className="text-neutral-500 shrink-0 mt-0.5" />
                <a href="mailto:kontakt@hilleroedsmoerrebroed.dk" className="hover:underline">
                  kontakt@hilleroedsmoerrebroed.dk
                </a>
              </div>
            </div>

            <div className="pt-2 border-t border-neutral-900 flex justify-between items-center text-[10px] text-neutral-500">
              <a
                href="https://www.findsmiley.dk/1447473"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-neutral-400 hover:text-white transition-colors"
              >
                <ShieldCheck size={12} className="text-emerald-500" />
                <span>Se Smiley-rapport</span>
                <ExternalLink size={8} />
              </a>
              <span>CVR: 41829381</span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
